import Card from "components/Card/Card";
import {
  ControlledCheckbox,
  ControlledInput,
} from "components/Card/ControlledInput";
import { useFileDOI } from "components/DOIHeaderSelect/useFileDOI";
import type { OptionType } from "components/inputs";
import {
  ControlledTextArea,
  MaskedInput,
  MultiSelect,
  Select,
  TextInput,
} from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import SectionBreak from "components/SectionBreak";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { usePatchDateOfInjuryByIdMutation } from "services/doi-api";
import { usePatchFileByIdMutation } from "services/file-api.ts";
import type { CreateFileData, DateOfInjury, FileType } from "types/DataTypes";
import { useSelector } from "utils/redux";

const AccidentInformationForm = ({
  fileData,
  isReadOnly = false,
}: {
  fileData: FileType;
  isReadOnly?: boolean;
}) => {
  const { fileId } = useParams<{ fileId: string }>();
  const [patchFileById] = usePatchFileByIdMutation();
  const constants = useSelector((state) => state.shared.constants);
  const [showAccidentInformation, setShowAccidentInformation] = useState(false);
  const [patchDateOfInjuryById] = usePatchDateOfInjuryByIdMutation();
  const [isResetting, setIsResetting] = useState(false);
  const prevSelectedDoiIdRef = useRef<number | null>(null);
  const [selectedStateDoiId, setSelectedStateDoiId] = useState<number | null>(
    null,
  );

  const {
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  const { dois, selectedDoiId, selectDoi, isLoading } = useFileDOI(fileData);

  const handleDoiSave = useCallback(
    async (field: string, value: string) => {
      if (!selectedDoiId) return;

      try {
        await patchDateOfInjuryById({
          id: selectedDoiId.toString(),
          data: {
            [field]: value || "", // never send null values, use empty string instead
          },
        });
      } catch (error) {
        console.error("Error saving DOI field:", error);
      }
    },
    [patchDateOfInjuryById, selectedDoiId],
  );

  // show the accident information form if the file has a 111 service or a claimant
  useEffect(() => {
    if (!fileData) return;

    if (
      !fileData.services?.some((service) => service.type === "111") ||
      (fileData?.claimants && fileData.claimants.length > 0)
    ) {
      setShowAccidentInformation(true);
    }
  }, [fileData]);

  // update form values when DOI selection changes
  useEffect(() => {
    if (!selectedDoiId || !dois) return;

    const doiObject = dois.find((doi) => doi.id === selectedDoiId);
    if (!doiObject) return;

    setIsResetting(true);
    setSelectedStateDoiId(selectedDoiId);

    // cast to DateOfInjury to access the fields we need
    const doi = doiObject as unknown as DateOfInjury;

    // check if we're switching DOIs - if so, don't keep dirty values
    const isSwitchingDois = prevSelectedDoiIdRef.current !== selectedDoiId;
    prevSelectedDoiIdRef.current = selectedDoiId;

    // When switching DOIs, directly update jurisdiction fields to prevent stale values
    if (isSwitchingDois) {
      // Update DOI-specific fields directly
      setValue("jurisdiction", doi.jurisdiction || "");
      setValue("wcmsa_zip_code", doi.wcmsa_zip_code || "");
      setValue("one_eleven_jurisdiction", doi.one_eleven_jurisdiction || "");
      setValue("accident_description", doi.accident_description || "");
      setValue("denied_conditions", doi.denied_conditions || "");
      setValue("accepted_conditions", doi.accepted_conditions || "");
      setValue(
        "description_of_claimants_job",
        doi.description_of_claimants_job || "",
      );
    }

    reset(
      {
        // DOI fields
        jurisdiction: doi.jurisdiction || "",
        wcmsa_zip_code: doi.wcmsa_zip_code || "",
        one_eleven_jurisdiction: doi.one_eleven_jurisdiction || "",
        accident_description: doi.accident_description || "",
        denied_conditions: doi.denied_conditions || "",
        accepted_conditions: doi.accepted_conditions || "",
        description_of_claimants_job: doi.description_of_claimants_job || "",

        // File fields
        date_referred: fileData?.date_referred || "",
        created: fileData?.created || "",
        insurance_types: fileData?.insurance_types || [],
        is_jones_act: fileData?.is_jones_act || false,
        is_longshore: fileData?.is_longshore || false,
        awarded_ssd: fileData?.awarded_ssd || false,
        applied_ssd: fileData?.applied_ssd || false,
        settlement_reached: fileData?.settlement_reached || false,
        estimated_settlement_amount:
          fileData?.estimated_settlement_amount || "",
        estimated_medical_settlement_value:
          fileData?.estimated_medical_settlement_value || "",
        reserve_settlement_amount: fileData?.reserve_settlement_amount || "",
        tentative_settlement_minimum:
          fileData?.tentative_settlement_minimum || "",
        tentative_settlement_maximum:
          fileData?.tentative_settlement_maximum || "",
        preferred_structure_settlement_broker:
          fileData?.preferred_structure_settlement_broker || "",
      },
      {
        // only keep dirty values if we're not switching DOIs
        keepDirtyValues: !isSwitchingDois,
      },
    );

    setIsResetting(false);
  }, [selectedDoiId, fileData, reset, setValue, dois]);

  const handleSave = useCallback(
    async (
      _api: "file",
      field: string,
      value: string | number | boolean | { value: string; label: string }[],
    ) => {
      if (field === "insurance_types") {
        const insuranceTypeMap: Record<string, string> = {
          L: "liability",
          E: "workers_comp",
          D: "no_fault",
        };

        const transformedValue = (
          value as Array<{ value: string; label: string; type?: string }>
        )
          .filter(
            (option) =>
              option?.value != null &&
              option.value !== "-- Select --" &&
              option.label !== "-- Select --",
          )
          .map((option) => ({
            type: option.type || insuranceTypeMap[option.value] || option.value,
          }));

        await patchFileById({
          id: fileId,
          data: {
            [field]: transformedValue,
          } as Partial<CreateFileData>,
        });
        return;
      }

      await patchFileById({
        id: fileId,
        data: {
          [field]: value,
        } as Partial<CreateFileData>,
      });
    },
    [patchFileById, fileId],
  );

  const options = useMemo(() => {
    return (
      dois?.map((doi) => ({
        value: doi.id,
        label: doi.date.substring(0, 10),
      })) || []
    );
  }, [dois]);

  const selectedOption = useMemo(
    () =>
      options.find((o) => o.value?.toString() === selectedDoiId?.toString()),
    [options, selectedDoiId],
  );

  if (!showAccidentInformation) return null;

  const handleDoiSaveWrapper = async (
    field: string,
    value: string,
  ): Promise<void> => {
    await handleDoiSave(field, value);
  };

  return (
    <Card heading="Accident Information">
      <Controller
        name="insurance_types"
        control={control}
        render={({ field: { value, onChange } }) => (
          <MultiSelect
            name="insurance_types"
            label="Insurance Types"
            // filter out options that are already selected
            options={
              constants?.PLAN_INSURANCE_TYPE?.filter((option) => {
                if (
                  option?.value == null ||
                  option.value === "-- Select --" ||
                  option.label === "-- Select --"
                ) {
                  return false;
                }

                const insuranceTypeMap: Record<string, string> = {
                  L: "liability",
                  E: "workers_comp",
                  D: "no_fault",
                };
                const optionType = insuranceTypeMap[option.value];
                return !value?.some(
                  (selected: { type: string }) => selected.type === optionType,
                );
              }) || []
            }
            value={value}
            readOnly={isReadOnly}
            onChange={(options) => {
              const typedOptions = options as {
                value: string;
                label: string;
              }[];
              onChange(typedOptions);
              handleSave("file", "insurance_types", typedOptions);
            }}
            errors={errors}
          />
        )}
      />

      <SectionBreak />

      {isLoading ? (
        <div>Loading DOIs...</div>
      ) : !dois?.length ? (
        <div>No dates of injury available</div>
      ) : (
        <Select
          name="doi"
          label="DOI"
          options={options}
          value={selectedOption}
          readOnly={isReadOnly}
          onChange={(o: OptionType) => selectDoi(o?.value?.toString() || null)}
        />
      )}

      <ControlledSelect
        key={`jurisdiction-${selectedStateDoiId}`}
        name="jurisdiction"
        control={control}
        readOnly={isReadOnly || !selectedDoiId || isResetting}
        label="WCMSA State"
        options={constants?.STATES || []}
        errors={errors}
        onSave={async (option) => {
          const optionValue = option?.value != null ? String(option.value) : "";
          await handleDoiSaveWrapper("jurisdiction", optionValue);
        }}
      />

      <ControlledInput
        key={`wcmsa-zip-${selectedStateDoiId}`}
        name="wcmsa_zip_code"
        componentType={MaskedInput}
        mask="zipcode-nine"
        control={control}
        label="WCMSA Zip Code"
        errors={errors}
        readOnly={isReadOnly || isResetting}
        onSave={(value) =>
          handleDoiSaveWrapper("wcmsa_zip_code", String(value || ""))
        }
      />

      <ControlledSelect
        key={`111-jurisdiction-${selectedStateDoiId}`}
        name="one_eleven_jurisdiction"
        control={control}
        readOnly={isReadOnly || !selectedDoiId || isResetting}
        label="111 Jurisdiction"
        options={constants?.JURISDICTIONS || []}
        errors={errors}
        onSave={async (option) => {
          const optionValue = option?.value != null ? String(option.value) : "";
          await handleDoiSaveWrapper("one_eleven_jurisdiction", optionValue);
        }}
      />

      <ControlledTextArea
        key={`accident-description-${selectedStateDoiId}`}
        name="accident_description"
        control={control}
        errors={errors}
        label="Accident Description"
        advancedEdit={true}
        readOnly={isReadOnly || isResetting}
        onSave={(value) =>
          handleDoiSaveWrapper("accident_description", String(value || ""))
        }
      />

      <ControlledTextArea
        key={`job-description-${selectedStateDoiId}`}
        name="description_of_claimants_job"
        control={control}
        errors={errors}
        label="Description of Claimnant's Job at time of injury"
        advancedEdit={true}
        readOnly={isReadOnly || isResetting}
        onSave={(value) =>
          handleDoiSaveWrapper(
            "description_of_claimants_job",
            String(value || ""),
          )
        }
      />

      <ControlledTextArea
        key={`accepted-conditions-${selectedStateDoiId}`}
        name="accepted_conditions"
        control={control}
        errors={errors}
        label="Accepted Conditions"
        advancedEdit={true}
        readOnly={isReadOnly || isResetting}
        onSave={(value) =>
          handleDoiSaveWrapper("accepted_conditions", String(value || ""))
        }
      />

      <ControlledTextArea
        key={`denied-conditions-${selectedStateDoiId}`}
        name="denied_conditions"
        control={control}
        errors={errors}
        label="Denied Conditions"
        advancedEdit={true}
        readOnly={isReadOnly || isResetting}
        onSave={(value) =>
          handleDoiSaveWrapper("denied_conditions", String(value || ""))
        }
      />

      <SectionBreak />

      <ControlledCheckbox
        control={control}
        name="is_jones_act"
        label="Is Jones Act"
        errors={errors}
        readOnly={isReadOnly}
        onSave={(value) => handleSave("file", "is_jones_act", value)}
      />

      <ControlledCheckbox
        control={control}
        name="is_longshore"
        label="Is Longshore"
        errors={errors}
        readOnly={isReadOnly}
        onSave={(value) => handleSave("file", "is_longshore", value)}
      />

      <SectionBreak />

      <ControlledCheckbox
        control={control}
        name="awarded_ssd"
        label="Awarded SSD"
        errors={errors}
        readOnly={isReadOnly}
        onSave={(value) => handleSave("file", "awarded_ssd", value)}
      />

      <ControlledCheckbox
        control={control}
        name="applied_ssd"
        label="Applied SSD"
        errors={errors}
        readOnly={isReadOnly}
        onSave={(value) => handleSave("file", "applied_ssd", value)}
      />

      <SectionBreak />

      <ControlledCheckbox
        control={control}
        name="settlement_reached"
        label="Settlement Reached"
        errors={errors}
        readOnly={isReadOnly}
        onSave={(value) => handleSave("file", "settlement_reached", value)}
      />

      <ControlledInput
        control={control}
        name="estimated_settlement_amount"
        label="Estimated Settlement Amount"
        errors={errors}
        readOnly={isReadOnly}
        onSave={(value) =>
          handleSave("file", "estimated_settlement_amount", value)
        }
        componentType={MaskedInput}
        mask="dollar"
      />

      <ControlledInput
        control={control}
        name="estimated_medical_settlement_value"
        label="Estimated Medical Settlement Value"
        errors={errors}
        readOnly={isReadOnly}
        onSave={(value) =>
          handleSave(
            "file",
            "estimated_medical_settlement_value",
            value || null,
          )
        }
        componentType={MaskedInput}
        mask="dollar"
      />

      <ControlledInput
        control={control}
        name="reserve_settlement_amount"
        label="Settlement Amount"
        errors={errors}
        readOnly={isReadOnly}
        onSave={(value) =>
          handleSave("file", "reserve_settlement_amount", value)
        }
        componentType={MaskedInput}
        mask="dollar"
      />

      <ControlledInput
        control={control}
        name="tentative_settlement_minimum"
        label="Tentative Settlement Minimum"
        errors={errors}
        readOnly={isReadOnly}
        onSave={(value) =>
          handleSave("file", "tentative_settlement_minimum", value)
        }
        componentType={MaskedInput}
        mask="dollar"
      />

      <ControlledInput
        control={control}
        name="tentative_settlement_maximum"
        label="Tentative Settlement Maximum"
        errors={errors}
        readOnly={isReadOnly}
        onSave={(value) =>
          handleSave("file", "tentative_settlement_maximum", value)
        }
        componentType={MaskedInput}
        mask="dollar"
      />

      <ControlledInput
        control={control}
        name="preferred_structure_settlement_broker"
        label="Preferred Structure Settlement Broker"
        errors={errors}
        readOnly={isReadOnly}
        onSave={(value) =>
          handleSave("file", "preferred_structure_settlement_broker", value)
        }
        componentType={TextInput}
        mask="dollar"
      />
    </Card>
  );
};

export default AccidentInformationForm;
